import { useEffect } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { PublicLayout } from "@/components/PublicLayout";
import { useI18n } from "@/i18n/I18nContext";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { BLOG_POSTS, getPost } from "@/data/blogPosts";
import { ArrowLeft, ArrowRight, CalendarDays } from "lucide-react";

export default function BlogPost() {
  const { slug } = useParams();
  const { tl, lang } = useI18n();
  const navigate = useNavigate();
  const post = getPost(slug);

  useEffect(() => {
    if (!post) return;
    const url = `https://frenchiesfitness.com/blog/${post.slug}`;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "blog-post-ldjson";
    script.textContent = JSON.stringify([
      { "@context": "https://schema.org", "@type": "Article", "headline": post.title.es, "description": post.excerpt.es, "image": post.image,
        "datePublished": post.date, "dateModified": post.date, "inLanguage": "es", "mainEntityOfPage": url,
        "author": { "@type": "Organization", "name": "Frenchies Fitness & Pilates" },
        "publisher": { "@type": "Organization", "name": "Frenchies Fitness & Pilates", "url": "https://frenchiesfitness.com" } },
      { "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://frenchiesfitness.com/" },
        { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://frenchiesfitness.com/blog" },
        { "@type": "ListItem", "position": 3, "name": post.title.es, "item": url } ] },
    ]);
    document.head.appendChild(script);
    return () => { document.getElementById("blog-post-ldjson")?.remove(); };
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;
  const others = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <PublicLayout>
      <Seo title={`${post.title.es} — Frenchies Coronado`} description={post.excerpt.es} path={`/blog/${post.slug}`} image={post.image} keywords="Coronado, Panamá, fitness, Pilates, gym" />
      <article className="mx-auto max-w-3xl px-5 py-12 lg:px-8" data-testid="blog-post">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="blog-back"><ArrowLeft className="h-4 w-4" /> Blog</Link>
        <h1 className="mt-4 font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl">{tl(post.title)}</h1>
        <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground"><CalendarDays className="h-4 w-4" /><time dateTime={post.date}>{post.date}</time> · Frenchies Fitness & Pilates, Coronado</div>
        <img src={post.image} alt={post.title[lang] || post.title.es} className="mt-8 h-72 w-full rounded-3xl object-cover sm:h-96" />
        <div className="mt-8 space-y-5 text-base leading-relaxed text-foreground/90 md:text-lg">
          {(post.body[lang] || post.body.es).map((para, i) => <p key={i}>{para}</p>)}
        </div>
        <div className="mt-10 rounded-3xl border border-primary/30 bg-primary/5 p-6 text-center">
          <p className="font-display text-xl font-semibold">{tl({ es: "¿Listo para entrenar en Coronado?", en: "Ready to train in Coronado?", fr: "Prêt à t'entraîner à Coronado ?" })}</p>
          <Button data-testid="blog-cta" onClick={() => navigate("/schedule")} className="mt-4 rounded-full">{tl({ es: "Reservar una clase", en: "Book a class", fr: "Réserver un cours" })} <ArrowRight className="ml-2 h-4 w-4" /></Button>
        </div>
      </article>
      <section className="mx-auto max-w-3xl px-5 pb-16 lg:px-8">
        <h2 className="mb-4 font-display text-xl font-semibold">{tl({ es: "Sigue leyendo", en: "Keep reading", fr: "À lire aussi" })}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {others.map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} className="rounded-2xl border border-border bg-card p-5 hover:border-primary/50">
              <div className="font-medium leading-snug">{tl(p.title)}</div>
              <div className="mt-1 text-sm text-muted-foreground">{tl(p.excerpt)}</div>
            </Link>
          ))}
        </div>
      </section>
    </PublicLayout>
  );
}
